import React, { useState, useEffect } from 'react';
import {
  Button,
  Alert,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input
} from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

// Friendly labels for the PackageTickerInterval values returned by the
// candidates endpoint. Mirrors the option list shown on the Package Tickers
// admin page so the wording is consistent across the two surfaces.
const INTERVAL_LABELS = {
  Daily: 'Daily',
  Weekly: 'Weekly',
  WeeklyCombinedFromDaily: 'Weekly (combined from daily candles)',
};

// Maps a PackageTickerInterval value to the CandleInterval string the
// migration endpoint will stamp on the stored rows. Kept client-side so
// the dialog can render the concrete interval the admin is about to use.
const RESOLVED_INTERVAL_LABELS = {
  Daily: 'Day',
  Weekly: 'Week',
  WeeklyCombinedFromDaily: 'Week',
};

export const AdminOriginalDataMigration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [migrating, setMigrating] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [dialogError, setDialogError] = useState(null);
  const [dialogResult, setDialogResult] = useState(null);

  useEffect(() => {
    loadCandidates();
  }, []);

  const loadCandidates = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getDataMigrationCandidates();
      setCandidates(data || []);
    } catch (exc) {
      console.error('Failed to load migration candidates:', exc);
      setError(exc.message || 'Failed to load migration candidates');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateClick = (candidate) => {
    setSelectedCandidate(candidate);
    setUpdateExisting(false);
    setDialogError(null);
    setDialogResult(null);
    setDialogOpen(true);
  };

  const handleProceed = async () => {
    if (!selectedCandidate) {
      return;
    }

    try {
      setMigrating(true);
      setDialogError(null);
      setDialogResult(null);

      const result = await apiClient.migrateOriginalData({
        tickerId: selectedCandidate.TickerId,
        packageTickerId: selectedCandidate.PackageTickerId ?? null,
        subfolder: selectedCandidate.Subfolder,
        fileName: selectedCandidate.FileName,
        updateExistingCandles: updateExisting
      });

      setDialogResult(result);
      setSuccess(
        `Migration completed successfully for ${selectedCandidate.TickerCode} (${result.Interval || 'Day'}): ` +
        `${result.CandlesInserted} inserted, ${result.CandlesUpdated} updated, ${result.CandlesSkipped} skipped`
      );
    } catch (exc) {
      console.error('Failed to migrate data:', exc);
      setDialogError(exc.message || 'Failed to migrate data');
    } finally {
      setMigrating(false);
    }
  };

  const handleCloseDialog = () => {
    if (!migrating) {
      setDialogOpen(false);
      setSelectedCandidate(null);
      setDialogError(null);
      setDialogResult(null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Original Data Source Data Migration</h2>
        <Button color="secondary" onClick={loadCandidates} disabled={loading}>
          <i className="fas fa-sync-alt me-2"></i>
          Refresh
        </Button>
      </div>

      <Alert color="info" className="mb-3" fade={false}>
        This page allows you to migrate historical candle data from the original data package CSV files into the database.
        Candidates come from every row in the <strong>PackageTickers</strong> admin table. Only entries with matching CSV files in the original data package are shown.
      </Alert>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      <Card>
        <CardHeader>
          <h5 className="mb-0">Migration Candidates ({candidates.length})</h5>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner />
              <p className="mt-2">Loading candidates...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-muted text-center py-4">
              No migration candidates found. Make sure you have:
              <ul className="text-start mt-2" style={{ maxWidth: '600px', margin: '0 auto' }}>
                <li>Uploaded the original data package</li>
                <li>Rows in the <strong>PackageTickers</strong> table</li>
                <li>Matching CSV files in the original data package</li>
              </ul>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Ticker ID</th>
                    <th>Source</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Subfolder</th>
                    <th>File Name</th>
                    <th>Candle interval</th>
                    <th>Registration</th>
                    <th style={{ width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate, index) => (
                    <tr key={index}>
                      <td>{candidate.TickerId}</td>
                      <td>{candidate.TickerSource}</td>
                      <td><code>{candidate.TickerCode}</code></td>
                      <td>{candidate.TickerName}</td>
                      <td><code>{candidate.Subfolder}</code></td>
                      <td><code>{candidate.FileName}</code></td>
                      <td>
                        {INTERVAL_LABELS[candidate.Interval] || candidate.Interval || '-'}
                      </td>
                      <td>
                        <span className="badge bg-primary" title={`PackageTicker row #${candidate.PackageTickerId}`}>
                          PackageTicker #{candidate.PackageTickerId}
                        </span>
                      </td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => handleMigrateClick(candidate)}
                        >
                          Migrate
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Migration Confirmation Dialog */}
      <Modal isOpen={dialogOpen} toggle={handleCloseDialog} size="lg">
        <ModalHeader toggle={handleCloseDialog}>
          Confirm Data Migration
        </ModalHeader>
        <ModalBody>
          {dialogError && (
            <Alert color="danger" className="mb-3" fade={false}>
              <strong>Error:</strong> {dialogError}
            </Alert>
          )}

          {dialogResult && (
            <Alert color="success" className="mb-3" fade={false}>
              <strong>Migration completed successfully!</strong>
              <div className="mt-2">
                <div><strong>Candles Inserted:</strong> {dialogResult.CandlesInserted}</div>
                <div><strong>Candles Updated:</strong> {dialogResult.CandlesUpdated}</div>
                <div><strong>Candles Skipped:</strong> {dialogResult.CandlesSkipped}</div>
                <div className="mt-2"><strong>Total Processed:</strong> {dialogResult.CandlesInserted + dialogResult.CandlesUpdated + dialogResult.CandlesSkipped}</div>
                {dialogResult.Interval && (
                  <div className="mt-2"><strong>Stored as interval:</strong> {dialogResult.Interval}</div>
                )}
              </div>
            </Alert>
          )}

          {selectedCandidate && !dialogResult && (
            <div>
              <p className="mb-3">
                Please review the migration details below and confirm:
              </p>

              <FormGroup>
                <Label><strong>Ticker ID:</strong></Label>
                <Input type="text" value={selectedCandidate.TickerId} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>Ticker Source:</strong></Label>
                <Input type="text" value={selectedCandidate.TickerSource} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>Ticker Code:</strong></Label>
                <Input type="text" value={selectedCandidate.TickerCode} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>Ticker Name:</strong></Label>
                <Input type="text" value={selectedCandidate.TickerName} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>Subfolder:</strong></Label>
                <Input type="text" value={selectedCandidate.Subfolder} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>File Name:</strong></Label>
                <Input type="text" value={selectedCandidate.FileName} disabled />
              </FormGroup>

              <FormGroup>
                <Label><strong>Candle interval (PackageTicker):</strong></Label>
                <Input
                  type="text"
                  value={INTERVAL_LABELS[selectedCandidate.Interval] || selectedCandidate.Interval || '-'}
                  disabled
                />
                <div className="form-text">
                  Candles will be stored as{' '}
                  <strong>
                    {RESOLVED_INTERVAL_LABELS[selectedCandidate.Interval] || 'Day'}
                  </strong>{' '}
                  rows (the original-package CSV already carries candles at the
                  ticker's intended periodicity — no aggregation is performed).
                </div>
              </FormGroup>

              <FormGroup>
                <Label><strong>Registration:</strong></Label>
                <Input
                  type="text"
                  value={`PackageTicker row #${selectedCandidate.PackageTickerId}`}
                  disabled
                />
              </FormGroup>

              <FormGroup check className="mt-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    disabled={migrating}
                  />
                  <strong>Update Existing Candles</strong>
                  <div className="form-text">
                    If checked, existing candle records will be updated with data from the CSV file.
                    If unchecked, existing records will be skipped and only new records will be inserted.
                  </div>
                </Label>
              </FormGroup>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {dialogResult ? (
            <Button color="primary" onClick={handleCloseDialog}>
              Close
            </Button>
          ) : (
            <>
              <Button color="secondary" onClick={handleCloseDialog} disabled={migrating}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleProceed} disabled={migrating}>
                {migrating ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  'Proceed'
                )}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};
